import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Person,
  Email,
  WorkOutline,
  VerifiedUser,
  Block,
  ArrowForward,
} from '@mui/icons-material';

import translateUserStatus from '../../../../helperFunctions/translateUserStatus';
import { User, UserStatus, UserRole } from '../../../../models/User';

type Props = {
  user: User;
  url?: string;
};

function UserCard({ user, url }: Props) {
  // Dodajemy stan do śledzenia, czy obraz się załadował poprawnie
  const [imageError, setImageError] = React.useState(false);

  // Funkcje dla statusu użytkownika
  const getUserStatusColor = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Active:
        return 'bg-green-50 text-green-700 border-green-200';
      case UserStatus.Inactive:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getUserStatusIcon = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Active:
        return <VerifiedUser fontSize="small" />;
      case UserStatus.Inactive:
        return <Block fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  // Funkcje dla roli użytkownika
  const getUserRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.Admin:
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case UserRole.Moderator:
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case UserRole.User:
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getUserRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.Admin:
        return <WorkOutline fontSize="small" />;
      case UserRole.Moderator:
        return <WorkOutline fontSize="small" />;
      case UserRole.User:
        return <Person fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  // Inicjały użytkownika dla awatara
  const getUserInitials = () => {
    return `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase();
  };

  // Losowy kolor tła dla awatara (ale spójny dla tego samego użytkownika)
  const getAvatarColor = () => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-amber-500',
      'bg-pink-500',
      'bg-indigo-500',
    ];

    // Generowanie pseudolosowego koloru bazując na id użytkownika
    const index = user.id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Dane o zamówieniach
  const totalOrders = user.orders?.length || 0;
  const totalSpent: number =
    user.orders?.reduce(
      (sum: any, order) =>
        parseFloat(sum) + parseFloat(order.totalPrice as any),
      0,
    ) || 0;

  return (
    <motion.div
      className={`bg-white rounded-lg border ${
        user.status === UserStatus.Inactive
          ? 'border-red-200'
          : 'border-gray-200'
      } overflow-hidden hover:shadow-md transition-shadow`}
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            {user.profilePicture && !imageError ? (
              <img
                src={user.profilePicture}
                alt={`${user.name} ${user.surname}`}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0 mr-3 border border-gray-200"
                onError={() => setImageError(true)}
              />
            ) : (
              <div
                className={`w-12 h-12 rounded-full ${getAvatarColor()} text-white flex items-center justify-center font-semibold text-lg flex-shrink-0 mr-3`}
              >
                {getUserInitials()}
              </div>
            )}

            <div>
              <h3 className="font-medium text-gray-900 text-lg">
                {user.name} {user.surname}
              </h3>
              <p className="text-sm text-gray-500 flex items-center">
                <Email fontSize="small" className="mr-1" />
                {user.email}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <div
              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${getUserRoleColor(user.role)}`}
            >
              {getUserRoleIcon(user.role)}
              {user.role}
            </div>

            <div
              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${getUserStatusColor(user.status)}`}
            >
              {getUserStatusIcon(user.status)}
              {translateUserStatus(user.status)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xs text-gray-500">Liczba zamówień</p>
            <p className="font-semibold text-sm">{totalOrders}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xs text-gray-500">Wartość zamówień</p>
            <p className="font-semibold text-sm">{totalSpent.toFixed(2)} zł</p>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            ID: {user.id.substring(0, 8)}...
          </div>

          <Link
            to={url || `${user.id}`}
            className="flex items-center text-primary font-medium hover:text-primary-dark transition-colors text-sm"
          >
            Szczegóły
            <ArrowForward fontSize="small" className="ml-1" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default UserCard;
